/**
 * Error Interceptor for Axios
 * 
 * Sets up axios response interceptor to catch all API errors
 * Integrates with Error Context to display global error messages
 * Classifies errors and determines retry behavior
 */

import { classifyError, shouldExcludeError } from './errorClassifier';
import errorConfig from '../config/errorConfig';

/**
 * Setup axios error interceptor
 * @param {Object} axiosInstance - The axios instance to attach interceptor to
 * @param {Object} errorContext - The Error Context object with showError method
 * @returns {number} The interceptor ID (can be used to eject later)
 */
export const setupErrorInterceptor = (axiosInstance, errorContext) => {
  if (!axiosInstance) {
    console.error('Axios instance is required for error interceptor setup');
    return null;
  }

  if (!errorContext || typeof errorContext.showError !== 'function') {
    console.error('Error context with showError method is required for error interceptor setup');
    return null;
  }

  // Add response interceptor
  const interceptorId = axiosInstance.interceptors.response.use(
    // Success handler - pass through successful responses
    (response) => response,
    
    // Error handler - intercept and process errors
    (error) => {
      // Check if error should be excluded from global error handling
      if (shouldExcludeError(error, errorConfig.excludedEndpoints)) {
        // Still throw the error for component-level handling
        return Promise.reject(error);
      }

      // Classify the error
      const classifiedError = classifyError(error);

      // If error classification returns null, skip global error display
      if (!classifiedError) {
        // Still throw the error for component-level handling
        return Promise.reject(error);
      }

      // Exclude authentication errors (401/403) from global error display
      // These should be handled by the existing AuthContext logout flow
      if (classifiedError.type === 'auth') {
        // Log for debugging but don't show global error
        if (errorConfig.enableConsoleLogging) {
          console.log('Authentication error detected - delegating to auth flow:', classifiedError);
        }
        // Still throw the error so AuthContext can handle it
        return Promise.reject(error);
      }

      // Create retry callback for retryable errors
      let retryCallback = null;
      if (classifiedError.retryable && error.config) {
        retryCallback = async () => {
          // Retry the original request with the same configuration
          return axiosInstance.request(error.config);
        };
      }

      // Show error in global error handler
      errorContext.showError(
        classifiedError.type,
        classifiedError.message,
        classifiedError.details,
        classifiedError.retryable,
        retryCallback
      );

      // Still throw the error to maintain backward compatibility
      // Components can still catch and handle errors locally if needed
      return Promise.reject(error);
    }
  );

  return interceptorId;
};

/**
 * Remove error interceptor from axios instance
 * @param {Object} axiosInstance - The axios instance to remove interceptor from
 * @param {number} interceptorId - The interceptor ID returned from setupErrorInterceptor
 */
export const removeErrorInterceptor = (axiosInstance, interceptorId) => {
  if (axiosInstance && interceptorId !== null && interceptorId !== undefined) {
    axiosInstance.interceptors.response.eject(interceptorId);
  }
};

/**
 * Initialize error interceptor with default axios instance
 * This is a convenience function for quick setup
 * @param {Object} axiosInstance - The axios instance
 * @param {Object} errorContext - The Error Context object
 * @returns {Function} Cleanup function to remove the interceptor
 */
export const initializeErrorInterceptor = (axiosInstance, errorContext) => {
  const interceptorId = setupErrorInterceptor(axiosInstance, errorContext);
  
  // Return cleanup function
  return () => {
    removeErrorInterceptor(axiosInstance, interceptorId);
  };
};

export default setupErrorInterceptor;
